import React, { useEffect, useState } from 'react';
import backgroundImage from '../assets/Sample/adrien-bruneau-8cpR_Yf0rQs-unsplash.jpg';
import searchIcon from '../assets/search-icon.svg';
import arrowIcon from '../assets/event-arrow.svg';
import { useNavigate } from 'react-router-dom';
import PageHero from '../components/PageHero';
import '../styles.css';
import { useQuery } from '@tanstack/react-query';
import { fetchEvents } from '../api/events';
import { API_BASE_URL } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';
import Dropdown from '../components/Dropdown';
import {
    ALL,
    buildYearOptions,
    buildMonthOptions,
    filterEvents,
    groupEventsByMonth,
} from './eventFilterUtils';

const formatTime = (dateString) =>
    new Date(dateString).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
    });

const formatTimeRange = (event) => {
    if (!event.start_date) return 'TBD';
    const start = formatTime(event.start_date);
    if (!event.end_date) return start;
    return `${start} - ${formatTime(event.end_date)}`;
};

export default function Event() {
    const navigate = useNavigate();
    const [year, setYear] = useState(ALL);
    const [month, setMonth] = useState(ALL);
    const [search, setSearch] = useState('');

    const {
        data: events = [],
        isLoading,
        error,
    } = useQuery({
        queryKey: ['events'],
        queryFn: fetchEvents,
        retry: 1, // Retry once on failure
    });

    // Ensure body overflow is restored when component mounts
    useEffect(() => {
        document.body.style.overflow = '';

        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    const routeChange = (eventId) => {
        const path = `/event/detail/${eventId}`;
        navigate(path);
    };

    if (isLoading) return <LoadingSpinner />;

    if (error) {
        const errorMessage =
            error.response?.data?.detail ||
            error.message ||
            'Error loading events. Please check if the backend server is running.';
        return (
            <div className="event-page">
                <div
                    className="error-message"
                    style={{
                        padding: '2rem',
                        textAlign: 'center',
                        color: '#d32f2f',
                        marginTop: 'var(--header-height)',
                    }}
                >
                    <h2>Error loading events</h2>
                    <p>{errorMessage}</p>
                    {process.env.NODE_ENV === 'development' && (
                        <p
                            style={{
                                fontSize: '0.9rem',
                                marginTop: '1rem',
                                color: '#666',
                            }}
                        >
                            Make sure the backend server is running at{' '}
                            {API_BASE_URL}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    const yearOptions = buildYearOptions(events);
    const monthOptions = buildMonthOptions(events, year);
    const monthGroups = groupEventsByMonth(
        filterEvents(events, { year, month, search })
    );

    // Narrowing the year can drop the selected month from the list; clear it
    // rather than leaving an invisible filter applied.
    const chooseYear = (nextYear) => {
        setYear(nextYear);
        const stillOffered = buildMonthOptions(events, nextYear).some(
            (option) => option.value === month
        );
        if (!stillOffered) setMonth(ALL);
    };

    const renderCard = (event) => (
        <li
            key={event.pk}
            className="event-card"
            onClick={() => routeChange(event.pk)}
        >
            <div className="image-section">
                <img
                    src={
                        event.photos && event.photos.length > 0
                            ? event.photos[0].file
                            : backgroundImage
                    }
                    alt={event.title}
                />
                <div className="date-badge">
                    {event.start_date
                        ? new Date(event.start_date).getDate()
                        : '?'}
                </div>
            </div>
            <div className="content-section">
                <div className="title">{event.title}</div>
                <div className="time">{formatTimeRange(event)}</div>
                {/* Clamped to two lines in CSS, so no JS truncation here: a fixed
                    character count would cut in a different place than the
                    rendered line break does. */}
                <div className="description">
                    {event.description || 'No description available'}
                </div>
                <div className="arrow-container">
                    <img src={arrowIcon} alt="" className="arrow" />
                </div>
            </div>
        </li>
    );

    return (
        <div className="event-page">
            <PageHero image={backgroundImage} title="EVENT" />
            <div className="part2">
                <div className="event-filter-row">
                    <div className="event-filters">
                        <div className="event-filter-select">
                            <Dropdown
                                value={year}
                                onChange={chooseYear}
                                options={yearOptions}
                                variant="underline"
                            />
                        </div>
                        <div className="event-filter-select">
                            <Dropdown
                                value={month}
                                onChange={setMonth}
                                options={monthOptions}
                                variant="underline"
                            />
                        </div>
                    </div>
                    <div className="search-bar">
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Search events"
                        />
                        <img src={searchIcon} alt="" className="search-icon" />
                    </div>
                </div>
                {monthGroups.length > 0 ? (
                    monthGroups.map(([label, monthEvents]) => (
                        <section key={label} className="event-month-group">
                            <div className="month-ornament" />
                            <h2 className="month-heading">{label}</h2>
                            <ul className="event-list">
                                {monthEvents.map(renderCard)}
                            </ul>
                        </section>
                    ))
                ) : (
                    <p className="event-empty">
                        {events.length === 0
                            ? 'No events yet.'
                            : 'No events match your search.'}
                    </p>
                )}
            </div>
        </div>
    );
}
